import { Box, SxProps, Typography } from "@mui/material";
import { Button, ButtonProps, Dialog } from "@volt_developers/components";
import { useTranslation } from "react-i18next";

import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { TEST_IDS as COMMON_TEST_IDS } from "constants/TestIds";

import type { MouseEvent, ReactNode } from "react";

export const TEST_IDS = {
  confirmationDialog: "confirmation-dialog",
  modalTitle: "modal-title-text",
  modalContent: "modal-content-text",
};

export interface ConfirmationDialogProps {
  onClose: (confirm: boolean) => void;
  title: string;
  buttonColor?: Exclude<ButtonProps["color"], "grey">;
  cancelLabel?: string;
  cancelLabelColor?: Exclude<ButtonProps["color"], "ghost-blue" | "ghost">;
  description?: ReactNode;
  hideCloseIcon?: boolean;
  isConfirmationButtonDisabled?: boolean;
  isOperationInProgress?: boolean;
  maxWidth?: Dialog.WrapperProps["maxWidth"];
  okLabel?: string;
  okLabelColor?: Exclude<ButtonProps["color"], "grey">;
  onCancel?: VoidFunction;
  onOk?: VoidFunction;
  open?: boolean;
  sxWrapper?: SxProps;
}

export function ConfirmationDialog({
  hideCloseIcon = false,
  open = false,
  onCancel,
  onClose,
  onOk,
  title,
  buttonColor = "default",
  description,
  okLabel,
  okLabelColor,
  cancelLabel,
  cancelLabelColor,
  maxWidth = "sm",
  isOperationInProgress,
  isConfirmationButtonDisabled,
  sxWrapper,
}: ConfirmationDialogProps) {
  const { t } = useTranslation("common");

  const handleCancel = () => {
    if (onCancel) {
      onCancel();

      return;
    }

    onClose(false);
  };

  const handleOk = () => {
    if (onOk) {
      onOk();

      return;
    }

    onClose(true);
  };

  const handleCloseOnIcon = (event: MouseEvent) => {
    if (hideCloseIcon) {
      return;
    }

    event.stopPropagation();
    onClose(false);
  };

  return (
    <Dialog.Wrapper
      data-testid={TEST_IDS.confirmationDialog}
      maxWidth={maxWidth}
      open={open}
      sx={sxWrapper}
      onClose={() => onClose(false)}
      fullWidth
    >
      <Dialog.Title
        component="div"
        data-testid={TEST_IDS.modalTitle}
        onClose={handleCloseOnIcon}
      >
        {title}
      </Dialog.Title>
      {Boolean(description) && (
        <Dialog.Content data-testid={TEST_IDS.modalContent}>
          <Typography variant="body1">{description}</Typography>
        </Dialog.Content>
      )}
      <Dialog.Actions>
        <Box sx={{ width: "100%" }}>
          <Button
            color={cancelLabelColor ?? "grey"}
            data-testid={COMMON_TEST_IDS.modalCancelButton}
            disabled={isOperationInProgress}
            size="large"
            style={{ whiteSpace: "nowrap" }}
            variant="outlined"
            onClick={handleCancel}
            fullWidth
          >
            {cancelLabel || t(COMMON_TRANSLATION_KEYS.cancel) || ""}
          </Button>
        </Box>
        <Box sx={{ width: "100%" }}>
          <Button
            color={okLabelColor ?? buttonColor}
            data-testid={COMMON_TEST_IDS.modalConfirmButton}
            disabled={isConfirmationButtonDisabled}
            loading={isOperationInProgress}
            size="large"
            style={{ whiteSpace: "nowrap" }}
            variant="solid"
            onClick={handleOk}
            fullWidth
          >
            {okLabel || t(COMMON_TRANSLATION_KEYS.Ok) || ""}
          </Button>
        </Box>
      </Dialog.Actions>
    </Dialog.Wrapper>
  );
}
